import React, { useState, useEffect, useRef } from 'react';
import { content, colors } from '../data/content';
import { contact, getWhatsAppLink } from '../data/contact';
import { Send, ShoppingCart, CheckCircle, AlertCircle } from 'lucide-react';

const WholesaleForm = ({ language }) => {
  const currentContent = content[language].wholesale;
  const isRTL = language === 'ar';
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    phone: '',
    email: '',
    city: '',
    quantity: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [focusedField, setFocusedField] = useState(null);
  const [errors, setErrors] = useState({});
  const formRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.style.opacity = '1';
            entry.target.style.transform = 'translateY(0)';
          }
        });
      },
      { threshold: 0.2 }
    );

    const currentForm = formRef.current;
    if (currentForm) {
      observer.observe(currentForm);
    }

    // Add CSS to remove red borders from browser validation and style select arrows
    const primaryColor = colors.primary;
    const style = document.createElement('style');
    style.textContent = `
      #wholesale input:invalid,
      #wholesale select:invalid,
      #wholesale textarea:invalid {
        border-color: #e5e7eb !important;
        box-shadow: none !important;
        outline: none !important;
      }
      #wholesale input:invalid:focus,
      #wholesale select:invalid:focus,
      #wholesale textarea:invalid:focus {
        border-color: ${primaryColor} !important;
        box-shadow: 0 0 0 4px ${primaryColor}15 !important;
        outline: none !important;
      }
      #wholesale input:invalid:hover,
      #wholesale select:invalid:hover,
      #wholesale textarea:invalid:hover {
        border-color: #e5e7eb !important;
        box-shadow: none !important;
      }
      #wholesale input,
      #wholesale select,
      #wholesale textarea {
        outline: none !important;
      }
      #wholesale input:focus,
      #wholesale select:focus,
      #wholesale textarea:focus {
        outline: none !important;
      }
      #wholesale select {
        appearance: none;
        -webkit-appearance: none;
        -moz-appearance: none;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%234B5563' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: ${isRTL ? 'left 20px center' : 'right 20px center'};
        background-size: 12px;
        padding-${isRTL ? 'left' : 'right'}: 44px !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (currentForm) {
        observer.unobserve(currentForm);
      }
      if (style.parentNode) {
        style.parentNode.removeChild(style);
      }
    };
  }, [isRTL]);

  // Validation functions
  const validateField = (name, value) => {
    const validationMessages = currentContent.form.validation;
    let error = '';
    
    switch (name) {
      case 'name':
        if (!value.trim()) {
          error = validationMessages.nameRequired;
        } else if (value.trim().length < 2) {
          error = validationMessages.nameMinLength;
        }
        break;
      case 'phone':
        if (!value.trim()) {
          error = validationMessages.phoneRequired;
        } else if (!/^[\d\s+\-()]+$/.test(value)) {
          error = validationMessages.phoneInvalid;
        } else if (value.replace(/\D/g, '').length < 8) {
          error = validationMessages.phoneTooShort;
        }
        break;
      case 'email':
        // Email is optional - only validate format if provided
        if (value.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          error = validationMessages.emailInvalid;
        }
        break;
      case 'city':
        if (!value.trim()) {
          error = validationMessages.cityRequired;
        }
        break;
      case 'quantity':
        if (!value.trim()) {
          error = validationMessages.quantityRequired;
        }
        break;
      default:
        break;
    }
    
    return error;
  };

  const validateForm = () => {
    const newErrors = {};
    const requiredFields = ['name', 'phone', 'city', 'quantity'];
    
    requiredFields.forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setFocusedField(null);
    
    // Validate field on blur
    const error = validateField(name, value);
    if (error) {
      setErrors({
        ...errors,
        [name]: error
      });
    } else {
      // Clear error if field is valid
      const newErrors = { ...errors };
      delete newErrors[name];
      setErrors(newErrors);
    }
  };

  const formatFormDataForWhatsApp = (data, lang) => {
    const labels = {
      en: {
        title: '🛒 *Wholesale Inquiry*',
        name: '👤 Name',
        company: '🏢 Company',
        phone: '📱 Phone',
        email: '📧 Email',
        city: '📍 City',
        quantity: '📦 Quantity',
        message: '💬 Message'
      },
      ar: {
        title: '🛒 *استفسار الجملة*',
        name: '👤 الاسم',
        company: '🏢 الشركة',
        phone: '📱 الهاتف',
        email: '📧 البريد الإلكتروني',
        city: '📍 المدينة',
        quantity: '📦 الكمية',
        message: '💬 الرسالة'
      }
    };

    const t = labels[lang];
    let message = `${t.title}\n\n`;
    message += `${t.name}: ${data.name}\n`;
    if (data.company) message += `${t.company}: ${data.company}\n`;
    message += `${t.phone}: ${data.phone}\n`;
    if (data.email) message += `${t.email}: ${data.email}\n`;
    message += `${t.city}: ${data.city}\n`;
    message += `${t.quantity}: ${data.quantity}\n`;
    if (data.message) message += `\n${t.message}:\n${data.message}`;
    
    return message;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all fields before submission
    if (!validateForm()) {
      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          errorElement.focus();
        }
      }
      return;
    }
    
    setIsSubmitting(true);

    try {
      const whatsappMessage = formatFormDataForWhatsApp(formData, language);
      const whatsappLink = getWhatsAppLink(contact.phone.whatsapp, whatsappMessage);
      
      window.open(whatsappLink, '_blank');
      
      setToast({
        show: true,
        message: currentContent.form.successMessage,
        type: 'success'
      });
      
      setFormData({
        name: '',
        company: '',
        phone: '',
        email: '',
        city: '',
        quantity: '',
        message: ''
      });
      
      setErrors({});
      setIsSubmitting(false);
      
      setTimeout(() => {
        setToast({ show: false, message: '', type: 'success' });
      }, 4000);
    } catch (error) {
      setIsSubmitting(false);
      setToast({
        show: true,
        message: currentContent.form.errorMessage,
        type: 'error'
      });
      setTimeout(() => {
        setToast({ show: false, message: '', type: 'error' });
      }, 4000);
    }
  };

  return (
    <section
      id="wholesale"
      style={{
        background: `linear-gradient(180deg, #ffffff 0%, #f0f9ff 50%, #ffffff 100%)`,
        padding: 'clamp(60px, 10vw, 120px) clamp(20px, 4vw, 40px)',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background Decoration */}
      <div style={{
        position: 'absolute',
        top: '10%',
        [isRTL ? 'right' : 'left']: '10%',
        width: '400px',
        height: '400px',
        background: `radial-gradient(circle, ${colors.primary}08 0%, transparent 70%)`,
        borderRadius: '50%',
        filter: 'blur(60px)'
      }}></div>

      <div style={{ 
        maxWidth: '900px', 
        margin: '0 auto', 
        padding: '0 clamp(20px, 4vw, 40px)',
        position: 'relative',
        zIndex: 10
      }}>
        {/* Section Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: 'clamp(48px, 8vw, 64px)',
          animation: 'fadeInUp 0.8s ease-out'
        }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 20px',
            background: `linear-gradient(135deg, ${colors.primary}10 0%, ${colors.secondary}10 100%)`,
            borderRadius: '50px',
            marginBottom: '20px',
            border: `1px solid ${colors.primary}20`
          }}>
            <ShoppingCart size={18} style={{ color: colors.primary }} />
            <span style={{
              fontSize: '14px',
              fontWeight: 600,
              color: colors.primary,
              fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif'
            }}>
              {isRTL ? 'طلب الجملة' : 'Wholesale Order'}
            </span>
          </div>

          <h2 style={{
            fontSize: 'clamp(2rem, 5vw, 3rem)',
            fontWeight: 800,
            color: colors.secondary,
            marginBottom: 'clamp(16px, 3vw, 24px)',
            fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
            background: `linear-gradient(135deg, ${colors.secondary} 0%, ${colors.primary} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            lineHeight: isRTL ? 1.65 : 1.2
          }}>
            {currentContent.title}
          </h2>
          
          <p style={{
            color: colors.text,
            fontSize: 'clamp(1.125rem, 2.5vw, 1.25rem)',
            fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
            lineHeight: isRTL ? 1.8 : 1.7,
            maxWidth: '700px',
            margin: '0 auto'
          }}>
            {currentContent.subtitle}
          </p>
        </div>
        
        <form
          ref={formRef}
          onSubmit={handleSubmit}
          noValidate
          style={{
            background: 'white',
            borderRadius: '32px',
            padding: 'clamp(32px, 5vw, 48px)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
            border: '1px solid rgba(0, 0, 0, 0.05)',
            position: 'relative',
            overflow: 'hidden',
            opacity: 0,
            transform: 'translateY(30px)',
            transition: 'all 0.8s ease-out'
          }}
        >
          {/* Form Background Pattern */}
          <div style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '200px',
            height: '200px',
            background: `radial-gradient(circle, ${colors.primary}05 0%, transparent 70%)`,
            borderRadius: '50%',
            filter: 'blur(40px)',
            transform: 'translate(30%, -30%)'
          }}></div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 'clamp(20px, 4vw, 24px)',
            marginBottom: '24px',
            position: 'relative',
            zIndex: 10
          }}>
            {/* Name */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: 600,
                marginBottom: '10px',
                color: colors.secondary,
                fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif'
              }}>
                {currentContent.form.name} <span style={{ color: colors.primary }}>*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                onFocus={() => setFocusedField('name')}
                onBlur={handleBlur}
                required
                style={{
                  width: '100%',
                  padding: '16px 20px',
                  borderRadius: '16px',
                  border: `2px solid ${
                    errors.name 
                      ? colors.primary 
                      : focusedField === 'name' 
                        ? colors.primary 
                        : '#e5e7eb'
                  }`,
                  outline: 'none',
                  fontSize: '16px',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
                  background: focusedField === 'name' || errors.name ? '#fff' : '#f9fafb',
                  boxShadow: errors.name || focusedField === 'name' 
                    ? `0 0 0 4px ${colors.primary}15` 
                    : 'none'
                }}
              />
              {errors.name && (
                <p style={{
                  color: colors.primary,
                  fontSize: '13px',
                  marginTop: '6px',
                  marginBottom: 0,
                  fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <AlertCircle size={14} />
                  {errors.name}
                </p>
              )}
            </div>
            
            {/* Company */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: 600,
                marginBottom: '10px',
                color: colors.secondary,
                fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif'
              }}>
                {currentContent.form.company}
              </label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                onFocus={() => setFocusedField('company')}
                onBlur={() => setFocusedField(null)}
                style={{
                  width: '100%',
                  padding: '16px 20px',
                  borderRadius: '16px',
                  border: `2px solid ${focusedField === 'company' ? colors.primary : '#e5e7eb'}`,
                  outline: 'none',
                  fontSize: '16px',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
                  background: focusedField === 'company' ? '#fff' : '#f9fafb',
                  boxShadow: focusedField === 'company' ? `0 0 0 4px ${colors.primary}15` : 'none'
                }}
              />
            </div>
            
            {/* Phone */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: 600,
                marginBottom: '10px',
                color: colors.secondary,
                fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif'
              }}>
                {currentContent.form.phone} <span style={{ color: colors.primary }}>*</span>
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                onFocus={() => setFocusedField('phone')}
                onBlur={handleBlur}
                required
                style={{
                  width: '100%',
                  padding: '16px 20px',
                  borderRadius: '16px',
                  border: `2px solid ${
                    errors.phone 
                      ? colors.primary 
                      : focusedField === 'phone' 
                        ? colors.primary 
                        : '#e5e7eb'
                  }`,
                  outline: 'none',
                  fontSize: '16px',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
                  background: focusedField === 'phone' || errors.phone ? '#fff' : '#f9fafb',
                  boxShadow: errors.phone || focusedField === 'phone' 
                    ? `0 0 0 4px ${colors.primary}15` 
                    : 'none'
                }}
              />
              {errors.phone && (
                <p style={{
                  color: colors.primary,
                  fontSize: '13px',
                  marginTop: '6px',
                  marginBottom: 0,
                  fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <AlertCircle size={14} />
                  {errors.phone}
                </p>
              )}
            </div>
            
            {/* Email (Optional) */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: 600,
                marginBottom: '10px',
                color: colors.secondary,
                fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif'
              }}>
                {currentContent.form.email} <span style={{ color: colors.textLight, fontSize: '12px' }}>({isRTL ? 'اختياري' : 'Optional'})</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onFocus={() => setFocusedField('email')}
                onBlur={handleBlur}
                style={{
                  width: '100%',
                  padding: '16px 20px',
                  borderRadius: '16px',
                  border: `2px solid ${
                    errors.email 
                      ? colors.primary 
                      : focusedField === 'email' 
                        ? colors.primary 
                        : '#e5e7eb'
                  }`,
                  outline: 'none',
                  fontSize: '16px',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
                  background: focusedField === 'email' || errors.email ? '#fff' : '#f9fafb',
                  boxShadow: errors.email || focusedField === 'email' 
                    ? `0 0 0 4px ${colors.primary}15` 
                    : 'none'
                }}
              />
              {errors.email && (
                <p style={{
                  color: colors.primary,
                  fontSize: '13px',
                  marginTop: '6px',
                  marginBottom: 0,
                  fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <AlertCircle size={14} />
                  {errors.email}
                </p>
              )}
            </div>
            
            {/* City */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: 600,
                marginBottom: '10px',
                color: colors.secondary,
                fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif'
              }}>
                {currentContent.form.city} <span style={{ color: colors.primary }}>*</span>
              </label>
              <select
                name="city"
                value={formData.city}
                onChange={handleChange}
                onFocus={() => setFocusedField('city')}
                onBlur={handleBlur}
                required
                style={{
                  width: '100%',
                  padding: '16px 20px',
                  borderRadius: '16px',
                  border: `2px solid ${
                    errors.city 
                      ? colors.primary 
                      : focusedField === 'city' 
                        ? colors.primary 
                        : '#e5e7eb'
                  }`,
                  outline: 'none',
                  fontSize: '16px',
                  background: focusedField === 'city' || errors.city ? '#fff' : '#f9fafb',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
                  cursor: 'pointer',
                  boxShadow: errors.city || focusedField === 'city' 
                    ? `0 0 0 4px ${colors.primary}15` 
                    : 'none'
                }}
              >
                <option value="">{isRTL ? 'اختر المدينة' : 'Select City'}</option>
                {currentContent.cities.map((city, index) => (
                  <option key={index} value={city}>{city}</option>
                ))}
              </select>
              {errors.city && (
                <p style={{
                  color: colors.primary,
                  fontSize: '13px',
                  marginTop: '6px',
                  marginBottom: 0,
                  fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <AlertCircle size={14} />
                  {errors.city}
                </p>
              )}
            </div>
            
            {/* Quantity */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: 600,
                marginBottom: '10px',
                color: colors.secondary,
                fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif'
              }}>
                {currentContent.form.quantity} <span style={{ color: colors.primary }}>*</span>
              </label>
              <select
                name="quantity"
                value={formData.quantity}
                onChange={handleChange}
                onFocus={() => setFocusedField('quantity')}
                onBlur={handleBlur}
                required
                style={{
                  width: '100%',
                  padding: '16px 20px',
                  borderRadius: '16px',
                  border: `2px solid ${
                    errors.quantity 
                      ? colors.primary 
                      : focusedField === 'quantity' 
                        ? colors.primary 
                        : '#e5e7eb'
                  }`,
                  outline: 'none',
                  fontSize: '16px',
                  background: focusedField === 'quantity' || errors.quantity ? '#fff' : '#f9fafb',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
                  cursor: 'pointer',
                  boxShadow: errors.quantity || focusedField === 'quantity' 
                    ? `0 0 0 4px ${colors.primary}15` 
                    : 'none'
                }}
              >
                <option value="">{isRTL ? 'اختر الكمية' : 'Select Quantity'}</option>
                {currentContent.quantities.map((qty, index) => (
                  <option key={index} value={qty}>{qty}</option>
                ))}
              </select>
              {errors.quantity && (
                <p style={{
                  color: colors.primary,
                  fontSize: '13px',
                  marginTop: '6px',
                  marginBottom: 0,
                  fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <AlertCircle size={14} />
                  {errors.quantity}
                </p>
              )}
            </div>
          </div>
          
          {/* Message */}
          <div style={{ marginBottom: '32px', position: 'relative', zIndex: 10 }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: 600,
              marginBottom: '10px',
              color: colors.secondary,
              fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif'
            }}>
              {currentContent.form.message}
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              onFocus={() => setFocusedField('message')}
              onBlur={() => setFocusedField(null)}
              rows="5"
              style={{
                width: '100%',
                padding: '16px 20px',
                borderRadius: '16px',
                border: `2px solid ${focusedField === 'message' ? colors.primary : '#e5e7eb'}`,
                outline: 'none',
                fontSize: '16px',
                resize: 'vertical',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
                background: focusedField === 'message' ? '#fff' : '#f9fafb',
                boxShadow: focusedField === 'message' ? `0 0 0 4px ${colors.primary}15` : 'none',
                minHeight: '120px'
              }}
            />
          </div>
          
          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              width: '100%',
              padding: '18px 32px',
              borderRadius: '16px',
              background: isSubmitting 
                ? `linear-gradient(135deg, #9a0818 0%, #7a0614 100%)` 
                : `linear-gradient(135deg, ${colors.primary} 0%, #9a0818 100%)`,
              color: 'white',
              fontWeight: 700,
              fontSize: '18px',
              border: 'none',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
              boxShadow: isSubmitting 
                ? 'none' 
                : `0 8px 24px ${colors.primary}40`,
              position: 'relative',
              zIndex: 10,
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = `0 12px 32px ${colors.primary}50`;
              }
            }}
            onMouseLeave={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = `0 8px 24px ${colors.primary}40`;
              }
            }}
          >
            {isSubmitting ? (
              <>
                <div className="loading-spinner" style={{
                  width: '20px',
                  height: '20px',
                  border: '3px solid rgba(255, 255, 255, 0.3)',
                  borderTopColor: 'white',
                  borderRadius: '50%'
                }}></div>
                <span>{isRTL ? 'جاري الإرسال...' : 'Submitting...'}</span>
              </>
            ) : (
              <>
                <span>{currentContent.form.submitButton}</span>
                <Send size={20} style={{ 
                  transform: isRTL ? 'scaleX(-1)' : 'none'
                }} />
              </>
            )}
          </button>
        </form>
        
        {/* Toast Notification */}
        {toast.show && (
          <div style={{
            position: 'fixed',
            bottom: 'clamp(80px, 12vw, 120px)',
            left: '50%',
            transform: 'translateX(-50%)',
            background: toast.type === 'success' 
              ? `linear-gradient(135deg, #10b981 0%, #059669 100%)` 
              : `linear-gradient(135deg, #ef4444 0%, #dc2626 100%)`,
            color: 'white',
            padding: '16px 32px',
            borderRadius: '16px',
            boxShadow: '0 12px 32px rgba(0, 0, 0, 0.2)',
            zIndex: 2000,
            animation: 'fadeInUp 0.3s ease-out',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
            fontSize: '16px',
            fontWeight: 600,
            maxWidth: '90%',
            textAlign: 'center'
          }}>
            {toast.type === 'success' ? (
              <CheckCircle size={24} />
            ) : (
              <AlertCircle size={24} />
            )}
            <span>{toast.message}</span>
          </div>
        )}
      </div>
    </section>
  );
};

export default WholesaleForm;
